// Строка нормы: статус вычислителя первым, раскрывается цитатой и разбором.

import { useLang, useT } from '../i18n'
import { verdictText } from '../map/bans'
import type { RuleNorm } from '../api'
import { quoteLabel } from './coverage'
import { valueText } from './terms'

// Тон статуса — тот же, что у кромки его группы (`RuleGroups.GROUP_EDGE`).
const TONE: Record<string, string> = {
  applies: 'text-coral',
  depends: 'text-buoy',
  excepted: 'text-ok',
}

export function RuleRow({ norm, open, onToggle, quoteLang, origLang }: {
  norm: RuleNorm
  quoteLang?: string
  origLang?: string
  open: boolean
  onToggle: () => void
}) {
  const t = useT()
  const { lang } = useLang()
  const v = norm.verdict
  return (
    <li className={`border-b border-hull-hair last:border-b-0 ${open ? 'bg-trench/60' : ''}`}>
      <button type="button" onClick={onToggle} aria-expanded={open}
        className={`grid w-full grid-cols-[4.25rem_1fr] gap-x-2 px-3 py-2 text-left hover:bg-haze focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-sonar ${open ? 'shadow-[inset_2px_0_0_rgb(var(--accent))]' : ''}`}>
        <span className="tnum break-words pt-px text-[12px] leading-snug text-mist">
          <span className="sr-only">{t('bans.card.point')} </span>{norm.point}
        </span>
        <span className="min-w-0">
          <span className="block text-[13px] leading-snug text-foam">{norm.title}</span>
          <span className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px] leading-snug">
            <span className={TONE[v.status] ?? 'text-faint'}>{verdictText(v, t)}</span>
            {norm.place === 'here' && <span className="text-foam">{t('rules.place.here')}</span>}
            {norm.place === 'edge' && <span className="text-buoy">{t('rules.place.edge')}</span>}
            {norm.place === 'unknown' && <span className="text-faint">{t('rules.place.unknown')}</span>}
            {norm.place === 'everywhere' && <span className="text-faint">{t('rules.place.everywhere')}</span>}
            {norm.place_code && !norm.drawn && norm.place !== 'unknown' && <span className="text-faint">{t('rules.place.notDrawn')}</span>}
            {norm.permits && <span className="text-ok">{t('rules.permits')}</span>}
          </span>
          <span className="mt-0.5 block text-[10px] text-faint">{norm.section}, {t(`rules.kind.${norm.kind}`)}</span>
        </span>
      </button>
      {open && (
        <div className="space-y-1.5 py-2 pl-[5.5rem] pr-3 text-[11px] leading-relaxed text-mist">
          <div className="text-[10px] text-faint">{quoteLabel(norm.quote_lang ?? quoteLang, lang, origLang)}</div>
          <blockquote className="border-l-2 border-hull pl-2 text-foam">{norm.quote}</blockquote>
          {norm.quote_original && (
            <details>
              <summary className="cursor-pointer text-[10px] text-faint hover:text-mist">
                {t('rules.showOriginal')}
              </summary>
              <blockquote lang="ru" className="mt-1 border-l-2 border-hull pl-2 text-mist">
                {norm.quote_original}
              </blockquote>
            </details>
          )}
          {norm.fishing_types.length > 0 && (
            <div><span className="text-faint">{t('rules.row.who')}: </span>
              {(norm.audiences?.length ? norm.audiences.map(a => t(`rules.audience.${a}`))
                : norm.fishing_types).join(', ')}</div>
          )}
          {norm.value && (
            <div><span className="text-faint">{t('rules.row.value')}: </span>
              {valueText(norm.value)}
              {norm.value.measure && ` (${norm.value.measure})`}</div>
          )}
          {norm.exceptions.length > 0 && (
            <div>
              <span className="text-faint">{t('rules.row.exceptions')}:</span>
              <ul className="list-disc pl-4">
                {norm.exceptions.map(e => (
                  <li key={e.text}>{e.text}{e.in_geometry && ` (${t('rules.row.cutOut')})`}</li>
                ))}
              </ul>
            </div>
          )}
          {norm.readings.length > 0 && (
            <div>
              <span className="text-faint">{t('rules.row.readings')}:</span>
              <ul className="list-disc pl-4">
                {norm.readings.map(r => (
                  <li key={r.text}>
                    {r.chosen ? `${t('rules.row.chosen')}: ` : `${t('rules.row.alternative')}: `}
                    {r.text} <span className="text-faint">— {r.why}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {norm.note && <div><span className="text-faint">{t('rules.row.note')}: </span>{norm.note}</div>}
        </div>
      )}
    </li>
  )
}
